import { useState, useEffect, useCallback, useMemo } from 'react';
import { PoopReport, Settings } from '../types';
import * as store from '../utils/store';

// Default settings for initial state
const DEFAULT_SETTINGS: Settings = {
  expiryDays: 30,
  blurMeters: 0,
  headerBgColor: '#9a3612',
  headerTextColor: '#ffffff',
  footerBgColor: '#9a3612',
  footerTextColor: '#ffffff',
  logoText: 'Pin the Poo',
  logoFont: 'The Seasons',
  logoFontSize: 1.5,
  logoColor: '#ffffff',
  tagline: 'Keeping our streets clean, one pin at a time',
  missionTitle: 'Our Mission',
  missionContent: '',
  privacyContent: '',
  termsContent: '',
  contactEmail: 'hello@pinthepoo.com',
  contactName: 'Pin the Poo Team'
};

// Main data hook
export function useStore() {
  const [poopReports, setPoopReports] = useState<PoopReport[]>([]);
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  // Load initial data and subscribe to updates
  useEffect(() => {
    let unsubscribeReports: (() => void) | null = null;
    let unsubscribeSettings: (() => void) | null = null;

    const init = async () => {
      setLoading(true);
      
      // Subscribe to real-time updates (works for both Firebase and localStorage)
      unsubscribeReports = store.subscribeToPoopReports((reports) => {
        setPoopReports(reports);
      });
      
      unsubscribeSettings = store.subscribeToSettings((newSettings) => {
        setSettings(newSettings);
      });

      // Also do initial load
      const [reports, initialSettings] = await Promise.all([
        store.getPoopReports(),
        store.getSettings()
      ]);
      
      setPoopReports(reports);
      setSettings(initialSettings);
      setLoading(false);
    };

    init();

    // Cleanup subscriptions
    return () => {
      if (unsubscribeReports) unsubscribeReports();
      if (unsubscribeSettings) unsubscribeSettings();
    };
  }, []);

  const loadData = useCallback(async () => {
    const [reports, newSettings] = await Promise.all([
      store.getPoopReports(),
      store.getSettings()
    ]);
    setPoopReports(reports);
    setSettings(newSettings);
  }, []);

  const addPoopReport = useCallback(async (report: PoopReport) => {
    await store.addPoopReport(report);
    // If not using Firebase (no real-time updates), manually refresh
    if (!store.useFirebase) {
      loadData();
    }
  }, [loadData]);

  const updatePoopReport = useCallback(async (id: string, updates: Partial<PoopReport>) => {
    await store.updatePoopReport(id, updates);
    if (!store.useFirebase) {
      loadData();
    }
  }, [loadData]);

  const deletePoopReport = useCallback(async (id: string) => {
    await store.deletePoopReport(id);
    if (!store.useFirebase) {
      loadData();
    }
  }, [loadData]);

  const updateSettings = useCallback(async (updates: Partial<Settings>) => {
    await store.updateSettings(updates);
    if (!store.useFirebase) {
      loadData();
    }
  }, [loadData]);

  // Get active reports (non-expired)
  const activePoopReports = useMemo(() => {
    const cutoff = Date.now() - settings.expiryDays * 24 * 60 * 60 * 1000;
    return poopReports.filter(r => r.hold || new Date(r.createdAt).getTime() >= cutoff);
  }, [poopReports, settings.expiryDays]);

  return {
    poopReports,
    activePoopReports,
    settings,
    loading,
    addPoopReport,
    updatePoopReport,
    deletePoopReport,
    updateSettings,
    refresh: loadData
  };
}

// Geolocation hook
export function useGeolocation() {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const requestLocation = useCallback(() => {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }

    // Check if we're in a secure context
    if (!window.isSecureContext) {
      console.log('Geolocation requires HTTPS. Use map tap to place pins.');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setAccuracy(position.coords.accuracy);
        setLoading(false);
        setError(null);
        setRetryCount(0);
      },
      (err) => {
        let message = 'Location unavailable';
        switch (err.code) {
          case err.PERMISSION_DENIED:
            message = 'Location permission denied';
            break;
          case err.POSITION_UNAVAILABLE:
            message = 'Location unavailable';
            break;
          case err.TIMEOUT:
            message = 'Location timed out. Tap to retry.';
            break;
        }
        setError(message);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0  // Force fresh location, no cache
      }
    );
  }, []);

  const retry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    requestLocation();
  }, [requestLocation]);

  return { location, accuracy, error, loading, requestLocation, retry, retryCount };
}

// Admin authentication hook
export function useAdmin() {
  const [isAdmin, setIsAdmin] = useState(() => {
    return sessionStorage.getItem('ptp_admin') === 'true';
  });

  const login = useCallback((password: string): boolean => {
    const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD || 'admin123';
    if (password === adminPassword) {
      sessionStorage.setItem('ptp_admin', 'true');
      setIsAdmin(true);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('ptp_admin');
    setIsAdmin(false);
  }, []);

  return { isAdmin, login, logout };
}
